import { type ReactNode, useCallback, useRef } from 'react';
import { Button, Typography } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';

const { Text } = Typography;

const LONG_PRESS_DELAY = 400;
const REPEAT_INTERVAL = 150;

interface DslrPropertyStepperProps {
  label: string;
  icon?: string;
  iconSize?: number;
  iconStyle?: React.CSSProperties;
  current: number | string;
  allowed: (number | string)[];
  formatValue: (v: number | string) => ReactNode;
  onStep: (newValue: number | string) => void;
  disabled?: boolean;
  isPending?: boolean;
  isReverted?: boolean;
}

export function DslrPropertyStepper({
  label,
  icon,
  iconSize = 20,
  iconStyle,
  current,
  allowed,
  formatValue,
  onStep,
  disabled = false,
  isPending = false,
  isReverted = false,
}: DslrPropertyStepperProps) {
  const isAuto = allowed.length === 0;
  const currentIndex = allowed.indexOf(current);
  const canDecrement = !disabled && !isAuto && currentIndex > 0;
  const canIncrement = !disabled && !isAuto && currentIndex < allowed.length - 1;

  const indexRef = useRef(currentIndex);
  indexRef.current = currentIndex;

  const timerRef = useRef<{ delay?: NodeJS.Timeout; interval?: NodeJS.Timeout }>({});

  const stopRepeat = useCallback(() => {
    clearTimeout(timerRef.current.delay);
    clearInterval(timerRef.current.interval);
    timerRef.current = {};
  }, []);

  const startRepeat = useCallback((direction: -1 | 1) => {
    stopRepeat();
    timerRef.current.delay = setTimeout(() => {
      timerRef.current.interval = setInterval(() => {
        const nextIdx = indexRef.current + direction;
        if (nextIdx < 0 || nextIdx >= allowed.length) {
          stopRepeat();
          return;
        }
        onStep(allowed[nextIdx]);
      }, REPEAT_INTERVAL);
    }, LONG_PRESS_DELAY);
  }, [allowed, onStep, stopRepeat]);

  const handleDecrement = () => {
    if (canDecrement) {
      onStep(allowed[currentIndex - 1]);
    }
  };

  const handleIncrement = () => {
    if (canIncrement) {
      onStep(allowed[currentIndex + 1]);
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 90, flexShrink: 0 }}>
        {icon && <MaterialIcon name={icon} size={iconSize} style={{ color: '#8c8c8c', ...iconStyle }} />}
        <Text strong style={{ textTransform: 'uppercase', fontSize: 12, whiteSpace: 'nowrap', position: 'relative', top: 2 }}>{label}</Text>
      </div>

      <Button.Group size="small" style={{ marginLeft: 16, flexShrink: 0 }}>
        <Button
          icon={<MaterialIcon name="remove" size={16} style={{ position: 'relative', top: -1 }} />}
          onClick={handleDecrement}
          onMouseDown={() => canDecrement && startRepeat(-1)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={() => canDecrement && startRepeat(-1)}
          onTouchEnd={stopRepeat}
          disabled={!canDecrement}
          style={{ minWidth: 32 }}
        />
        <Button
          icon={<MaterialIcon name="add" size={16} style={{ position: 'relative', top: -1 }} />}
          onClick={handleIncrement}
          onMouseDown={() => canIncrement && startRepeat(1)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={() => canIncrement && startRepeat(1)}
          onTouchEnd={stopRepeat}
          disabled={!canIncrement}
          style={{ minWidth: 32 }}
        />
      </Button.Group>

      <div style={{ flex: 1, marginLeft: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
        {!isAuto && (
          <Text
            className={isPending ? 'dslr-value-pending' : isReverted ? 'dslr-value-reverted' : undefined}
            style={{
              fontFamily: 'monospace',
              fontSize: 13,
              color: isPending ? '#1677ff' : isReverted ? undefined : '#595959',
              whiteSpace: 'nowrap',
              position: 'relative',
              top: 1,
            }}
          >
            {formatValue(current)}
          </Text>
        )}
        {isPending && (
          <MaterialIcon name="sync" size={14} className="dslr-sync-spin" style={{ color: '#1677ff' }} />
        )}
      </div>
    </div>
  );
}
